/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Grid, Box } from "@material-ui/core";
import { TitleDivider } from "./titleDivider";
import Alert from "@material-ui/lab/Alert";
import { observer } from "mobx-react";
import { createCurrencyField, createTextField, FileUpload, FileUploadArea } from "@roc/ui";
import { InternalValuationReviewStore } from "../stores/internalValuationReviewStore";
import { Button } from '@roc/ui';
import { useCallback, useEffect, useState } from 'react';
import { useDocumentStore } from '@roc/feature-documents';
import { UNDERWRITING } from '@roc/feature-utils';
import {
  formatDate,
} from '@roc/feature-utils';

interface ValuationFormProps {
  internalValuationReviewStore: InternalValuationReviewStore;
  readOnly: boolean;
}

export const ValuationForm = observer(({ internalValuationReviewStore, readOnly }: ValuationFormProps) => {

  const valuationFormStore = internalValuationReviewStore.valuationFormStore;
  const appraiser = valuationFormStore.form.fields.appraiser.value;

  const loanId = internalValuationReviewStore.loanId;
  const propertyId = internalValuationReviewStore.propertyId;

  return (
    <Grid container spacing={2}>
      <Grid item xs={4}>
        {createCurrencyField({
          label: 'As-Repaired Value',
          fieldName: 'asRepairedValue',
          testId: 'asRepairedValue',
          store: valuationFormStore,
          standaloneLabel: false,
          disabled: readOnly
        })}
      </Grid>
      <Grid item xs={4}>
        {createCurrencyField({
          label: 'As-Is Value',
          fieldName: 'asIsValue',
          testId: 'asIsValue',
          store: valuationFormStore,
          standaloneLabel: false,
          disabled: readOnly
        })}
      </Grid>
      <Grid item xs={4}>
        {createCurrencyField({
          label: 'Site Value',
          fieldName: 'siteValue',
          testId: 'siteValue',
          store: valuationFormStore,
          standaloneLabel: false,
          disabled: readOnly
        })}
      </Grid>
      <Grid item xs={12}>
        {createTextField({
          label: 'Value Reconciliation Comments',
          fieldName: 'valueReconciliationComments',
          testId: 'valueReconciliationComments',
          store: valuationFormStore,
          standaloneLabel: false,
          multiline: true,
          minRows: 4,
          disabled: readOnly
        })}
      </Grid>
      <Grid item xs={6}>
        <TitleDivider title="Appraiser" />
        <Alert severity={appraiser ? "info" : "warning"}>{appraiser || "Not available"}</Alert>
      </Grid>
      {loanId && propertyId && (<UploadManualReport loanId={loanId} propertyId={propertyId} readOnly={readOnly} />)}
    </Grid>
  );
});

export type InternalValuationReportProps = {
  loanId: number,
  propertyId: number
  readOnly: boolean,
}

const UploadManualReport = observer(({ loanId, propertyId, readOnly }: InternalValuationReportProps) => {
  const { documentStore } = useDocumentStore();
  const [document, setDocument] = useState<any>();

  const fetchPropertyLoanDocument = useCallback(async () => {
    const responseDoc = await documentStore.getPropertyLoanDocument(loanId, "Valuation Team Report", propertyId);
    setDocument(responseDoc);
  }, [loanId, propertyId, documentStore]);


  useEffect(() => {
    fetchPropertyLoanDocument();
  }, [fetchPropertyLoanDocument]);

  if (!document) {
    return null;
  }

  const hasFileUploaded = !!document.path;

  const message = !hasFileUploaded
    ? `${document.taskCode} has not been uploaded`
    : `${document.taskCode} was last uploaded at ${formatDate(document.lastUpdateDate)}`;

  const severity = hasFileUploaded ? "info" : "warning";

  const onUpload = (uploads: FileUpload[], successCallback) => {
    documentStore.uploadDocument(uploads[0].file, document, UNDERWRITING,
      () => {
        fetchPropertyLoanDocument();
        successCallback && successCallback();
      })
  }

  return (
    <Grid item xs={6}>
      <TitleDivider title="Valuation Team Report" />
      <Alert severity={severity}>{message}</Alert>
      {!readOnly && (
        <Dropzone onUpload={onUpload} />
      )}
    </Grid>);
});

interface DropzoneProps {
  onUpload: (items: any, successCallback: () => void) => void;
}

const Dropzone = ({ onUpload }: DropzoneProps) => {
  const [items, setItems] = useState([]);

  const onUploadHandler = () => {
    onUpload(items, () => setItems([]));
  }

  return (
    <Box display="flex" flexDirection="column">
      <FileUploadArea items={items} setItems={setItems} />
      <Box flex="1" display="flex" justifyContent="flex-end" gridGap="8px">
        <Button
          testId="cancel"
          variant="outlined"
          color="primary"
          onClick={() => setItems([])}
          disabled={items.length === 0}
        >
          Clear
        </Button>
        <Button
          testId="upload"
          variant="contained"
          color="primary"
          onClick={onUploadHandler}
          disabled={items.length === 0}
        >
          Upload
        </Button>
      </Box>
    </Box>);
}